import React from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { formatDateAsItIs } from "../../helper/common";
import { isOrganizationRole } from "../../helper/accountType";
import ProfilePhoto from "./ProfilePhoto";

const LINK_PLATFORMS = [
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "YouTube" },
  { key: "alltrails", label: "AllTrails" },
  { key: "onx", label: "onX" },
  { key: "website", label: "Website" },
];

function Section({ title, children }) {
  return (
    <section className="mb-4">
      <h6 className="fs-16 fw-600 border-bottom pb-2 mb-3">{title}</h6>
      <Row className="g-3">{children}</Row>
    </section>
  );
}

function Detail({ label, value, md = 4 }) {
  return (
    <Col md={md} sm={6}>
      <p className="fs-14 text-light mb-1">{label}</p>
      <p className="fs-16 fw-500 mb-0" style={{ wordBreak: "break-word" }}>
        {value || value === 0 ? value : "-"}
      </p>
    </Col>
  );
}

function Stat({ label, value }) {
  return (
    <Col xs={4}>
      <div className="border rounded-3 py-3 px-2 text-center h-100">
        <p className="fs-24 fw-600 mb-1">{value || 0}</p>
        <p className="fs-14 text-light mb-0">{label}</p>
      </div>
    </Col>
  );
}

function UserViewModal({ show, handleClose, selectedUser }) {
  const roleName = selectedUser?.role?.name || selectedUser?.role?.role_name || "";
  const isOrganization = isOrganizationRole(roleName);

  const linkRows = LINK_PLATFORMS
    .map(({ key, label }) => ({ key, label, url: selectedUser?.externalLinks?.[key] }))
    // AO 2.0 (A4): only web links the user added are listed
    .filter(({ url }) => typeof url === "string" && /^https?:\/\//.test(url));

  const interests = Array.isArray(selectedUser?.interests) ? selectedUser.interests : [];
  const owner = selectedUser?.owner;
  const ownerName = [owner?.firstName, owner?.lastName].filter(Boolean).join(" ");

  const getDeviceType = () => {
    if (!selectedUser?.deviceType) return "-";
    const deviceType = selectedUser.deviceType.toLowerCase();
    if (deviceType === "android") return "Android";
    if (deviceType === "ios") return "iOS";
    return selectedUser.deviceType.charAt(0).toUpperCase() + selectedUser.deviceType.slice(1);
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isOrganization ? "View Organization" : "View User"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProfilePhoto user={selectedUser} />
        <Section title={isOrganization ? "Organization details" : "Personal details"}>
          {isOrganization ? (
            <Detail label="Organization Name" value={selectedUser?.firstName} />
          ) : (
            <>
              <Detail label="First Name" value={selectedUser?.firstName} />
              <Detail label="Last Name" value={selectedUser?.lastName} />
            </>
          )}
          <Detail label="Username" value={selectedUser?.userName} />
          <Detail label="Email" value={selectedUser?.email} />
          {!isOrganization && (
            <Detail
              label="Date of Birth"
              value={selectedUser?.dateOfBirth ? formatDateAsItIs(selectedUser.dateOfBirth) : ""}
            />
          )}
          <Detail label="Location" value={selectedUser?.address} />
          <Detail label="Device Type" value={getDeviceType()} />
          <Detail label="Joined Date" value={formatDateAsItIs(selectedUser?.createdAt)} />
          <Detail label="Bio" value={selectedUser?.bio} md={12} />
        </Section>

        {isOrganization && (
          <Section title="Owner details">
            <Detail label="Owner Name" value={ownerName} />
            <Detail label="Owner Email" value={owner?.email} />
            <Detail
              label="Owner Date of Birth"
              value={owner?.dateOfBirth ? formatDateAsItIs(owner.dateOfBirth) : ""}
            />
          </Section>
        )}

        <Section title="External links">
          {linkRows.length === 0 ? (
            <Detail label="Links" value="" md={12} />
          ) : (
            linkRows.map(({ key, label, url }) => (
              <Col md={6} key={key}>
                <p className="fs-14 text-light mb-1">{label}</p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fs-16 fw-500"
                  style={{ wordBreak: "break-all" }}
                >
                  {url}
                </a>
              </Col>
            ))
          )}
        </Section>

        {/* AO 2.0 (B3): interests with their skill level (read-only) */}
        <Section title="Skills & levels">
          {interests.length === 0 ? (
            <Detail label="Skills" value="" md={12} />
          ) : (
            interests.map(({ _id, title, skillLevel }) => (
              <Detail key={_id} label={title} value={skillLevel?.name || "Not set"} md={4} />
            ))
          )}
          {selectedUser?.showSkillLevels === false && (
            <Col md={12}>
              <small className="text-light">Hidden from the public profile</small>
            </Col>
          )}
        </Section>

        <Section title="Activity">
          <Stat label="Total Posts" value={selectedUser?.activity} />
          <Stat label="Total Events" value={selectedUser?.event} />
          <Stat label="Total Likes" value={selectedUser?.like} />
        </Section>
      </Modal.Body>
    </Modal>
  );
}

export default UserViewModal;
